import {
  Checkbox,
  DataTable,
  IconTooltip,
  LeaderGroupCombobox,
  ModuleWrapper,
  UserCombobox,
} from '@/components'
import { Button } from '@/components_deprecated'
import { Input } from '@/components_deprecated/Input'
import type { ExportFormat } from '@/modules/exports'
import {
  useBreadcrumbOverride,
  useNodes,
  useOnErrorAuto,
  useParam,
  useUser,
} from '@/utils/hooks'
import { useMutation, useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Link, Navigate } from 'react-router'
import styled from 'styled-components'
import { EXPORT_SALARY_BASE } from './mutations'
import { SALARY_BASE, SALARY_BASE_ENTRIES } from './queries'
import { usePrompt } from '@ur-solutions/react-components'
import { ExportSalaryBaseEntriesPrompt } from '../companies/ExportSalaryBaseEntriesPrompt'
import { format } from '@/utils'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'

const TableFilter = styled.div`
  display: flex;
  margin-bottom: 1rem;
  padding: 1rem 0;
  background-color: ${props => props.theme.colors.white};
  border-radius: 16px;

  ${props => props.theme.layout.mobile} {
    & > div {
      width: 100%;
      padding: 1rem;
    }
    flex-direction: column;
  }
`

const FilterBlock = styled.div`
  flex-basis: 0;
  flex: 1 1 0px;
  padding: 0 1rem;
  & > label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    width: 100%;
    color: ${props => props.theme.colors.gray5};
  }
  ${props => props.theme.layout.mobile} {
    display: flex;
    flex-direction: column;
  }
`

export const SalaryBase: React.JSX.CFC = () => {
  const translations = useTranslate({
    name: 'common.name',
    created: 'common.created',
    edit: 'common.edit',
    delete: 'common.delete',
    user: 'common.user',
    leaderGroups: 'companies.leader-groups',
    noData: 'common.no-data',

    filter: 'common.filter',

    userFilter: 'common.user-filter',
    departmentFilter: 'common.department-filter',
    facility: 'common.facility',
    salaryCodeFilter: 'common.salary-code-filter',

    salaryBase: 'common.salary-base',
    salaryBaseLines: 'salary.salary-base-lines',
    externalId: 'common.external-id',
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    initialAmount: 'salary.initial-amount',
    department: 'common.department',
    export: 'common.export',
    onlyNonExported: 'salary.only-non-exported',
    items: ['salary.table.num-items', { n: 0 }],
  })

  const salaryBaseId = useParam('salaryBaseId')
  const me = useUser()

  const { formValues, formChangeHandler: handler } = useForm<{
    user: string
    leaderGroups: string[]
    onlyNonExported: boolean
    orderBy: string
  }>({
    values: {
      user: '',
      leaderGroups: [],
      onlyNonExported: false,
      orderBy: 'user__lastName',
    },
  })

  const onErrorAuto = useOnErrorAuto()
  const addPrompt = usePrompt()

  const { data, loading } = useQuery(SALARY_BASE, {
    variables: {
      id: salaryBaseId,
    },
    fetchPolicy: 'cache-and-network',
  })

  const {
    data: entryData,
    loading: entryLoading,
    refetch,
  } = useQuery(SALARY_BASE_ENTRIES, {
    variables: {
      id: salaryBaseId,
      user: formValues.user ?? undefined,
      leaderGroups: formValues.leaderGroups ?? undefined,
      onlyNonExported: formValues.onlyNonExported,
      orderBy: formValues.orderBy,
    },
    fetchPolicy: 'cache-and-network',
  })

  const [exportSalaryBase, { loading: exportLoading }] = useMutation(
    EXPORT_SALARY_BASE,
    {
      onError: onErrorAuto(),
    }
  )

  const salaryBase = data?.salaryBase

  const salaryBaseDate = new Date(
    salaryBase?.year ?? 0,
    (salaryBase?.month ?? 1) - 1
  )

  const salaryBaseTitle =
    translations.salaryBase + ` ${format(salaryBaseDate, 'MMMM yyyy')}`

  useBreadcrumbOverride(salaryBaseId, salaryBaseTitle)
  const entries = useNodes(entryData?.allSalaryBaseEntries)

  async function handleExport() {
    const { data } = await addPrompt<{
      exportFormat: ExportFormat
      leaderGroups: string[]
      onlyNonExported: boolean
    } | null>(resolve => (
      <ExportSalaryBaseEntriesPrompt
        salaryBaseId={salaryBaseId}
        onSubmit={resolve}
        initialLeaderGroups={formValues.leaderGroups}
        initialOnlyNonExported={formValues.onlyNonExported}
      />
    ))
    if (!data) return

    await exportSalaryBase({
      variables: {
        salaryBase: salaryBaseId,
        user: me.id,
        exportFormat: data.exportFormat,
        leaderGroups: data.leaderGroups,
        onlyNonExported: data.onlyNonExported,
      },
    })
    void refetch()
  }

  const column = createColumnHelper<(typeof entries)[number]>()

  const tableColumns = useMemo(
    () => [
      column.accessor('user', {
        header: translations.user,
        enableSorting: true,
        cell(info) {
          const user = info.getValue()
          const userText = user.internalNumber
            ? `(${user.internalNumber}) ${user.fullName}`
            : user.fullName

          return (
            <Link
              className="text-inherit"
              to={`/users/${user.id}`}
              target="_blank"
            >
              {userText}
            </Link>
          )
        },
      }),
      column.accessor('departmentId', {
        header: translations.department,
        enableSorting: true,
        cell(info) {
          return (
            <td>
              <Input
                type="text"
                value={info.getValue()}
                onChange={() => {}}
                disabled
                grayed
                fullWidth
              />
            </td>
          )
        },
      }),
      column.accessor('facilityId', {
        header: translations.facility,
        enableSorting: true,
        cell(info) {
          return (
            <td>
              <Input
                type="text"
                value={info.getValue()}
                onChange={() => {}}
                disabled
                grayed
                fullWidth
              />
            </td>
          )
        },
      }),
      column.accessor('salaryId', {
        enableSorting: true,
        header: () => translations.externalId,
        cell(info) {
          return (
            <td>
              <Input
                type="text"
                value={info.getValue()}
                onChange={() => {}}
                disabled
                grayed
                fullWidth
              />
            </td>
          )
        },
      }),
      column.accessor('amount', {
        header: translations.amount,
        enableSorting: true,
        sortingFn: (a, b) => a.original.amount - b.original.amount,
        cell(info) {
          return (
            <td>
              <Input
                type="text"
                value={info.getValue().toString()}
                onChange={() => {}}
                disabled
                grayed
                fullWidth
              />
            </td>
          )
        },
      }),
      column.accessor('exportTimestamp', {
        header: '',
        meta: { narrow: true, center: true },
        cell: info => {
          const entry = info.row.original

          return (
            <td>
              {entry.exportTimestamp && (
                <IconTooltip icon="download">
                  <div>
                    {format(
                      new Date(entry.exportTimestamp),
                      'dd.MM.yyyy HH:mm'
                    )}
                  </div>
                </IconTooltip>
              )}
            </td>
          )
        },
      }),
    ],
    [column, translations]
  )

  if (!loading && !salaryBase) return <Navigate to="/salary-bases" />

  return (
    <ModuleWrapper
      title={salaryBaseTitle}
      loading={loading}
      headerRight={
        !entryLoading && (
          <Button onClick={handleExport} disabled={exportLoading}>
            {translations.export}
          </Button>
        )
      }
    >
      <TableFilter>
        <FilterBlock>
          <label>{translations.userFilter}</label>
          <UserCombobox
            mode="single"
            value={formValues.user}
            filterSalaryCalculation
            onChange={handler('user')}
          />
        </FilterBlock>

        <FilterBlock>
          <label>{translations.leaderGroups}</label>
          <LeaderGroupCombobox
            mode="multiple"
            value={formValues.leaderGroups}
            onChange={handler('leaderGroups')}
          />
        </FilterBlock>
        <FilterBlock>
          <label>{translations.onlyNonExported}</label>
          <Checkbox
            value={formValues.onlyNonExported}
            onChange={handler('onlyNonExported')}
          />
        </FilterBlock>
      </TableFilter>
      <DataTable
        columns={tableColumns}
        data={entries}
        pagination={{
          itemsText: n => translations.items({ n }),
          pageSize: 20,
        }}
        initialSort={[{ id: 'user', desc: false }]}
        inCard
      />
    </ModuleWrapper>
  )
}
